import daikon from 'daikon';

export const isDicomFile = (item) => {
  // Only treat as DICOM if explicitly marked as such
  if (item.fileType === 'application/dicom') return true;
  
  // Check by file extension - be more strict
  const fileName = item.remark || item.title || '';
  const extension = fileName.toLowerCase();
  
  // Only consider it DICOM if it has the actual DICOM extensions
  const isDicomExtension = extension.endsWith('.dcm') || extension.endsWith('.dicom');
  
  // Additional check: if it's in XRay category but has image extensions, it's probably not DICOM
  const hasImageExtension = extension.endsWith('.jpg') || extension.endsWith('.jpeg') || 
                           extension.endsWith('.png') || extension.endsWith('.gif') || 
                           extension.endsWith('.bmp') || extension.endsWith('.tiff') ||
                           extension.endsWith('.webp');
  
  // Never treat regular image files as DICOM
  if (hasImageExtension) return false;
  
  // Only return true if we have explicit DICOM indicators
  return isDicomExtension;
};

export const loadDicomWithDaikon = async (imageUrl) => {
  try {
    console.log('Loading DICOM with Daikon from URL:', imageUrl);
    
    // Fetch the DICOM file
    const response = await fetch(imageUrl);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const arrayBuffer = await response.arrayBuffer();
    console.log('DICOM file loaded, size:', arrayBuffer.byteLength);
    
    // Parse DICOM with daikon
    const image = daikon.Series.parseImage(new DataView(arrayBuffer));
    
    if (!image) {
      throw new Error('Failed to parse DICOM image');
    }
    
    console.log('DICOM parsed successfully:', {
      rows: image.getRows(),
      cols: image.getCols(),
      dataLength: image.getInterpretedData().length,
      seriesDescription: image.getSeriesDescription(),
      imageType: image.getImageType()
    });
    
    return image;
    
  } catch (error) {
    console.error('Error loading DICOM with Daikon:', error);
    throw new Error(`Daikon DICOM loading failed: ${error.message}`);
  }
};

export const renderDicomToCanvas = async (dicomImage, canvas) => {
  try {
    const rows = dicomImage.getRows();
    const cols = dicomImage.getCols();
    const pixelData = dicomImage.getInterpretedData();
    
    console.log('Rendering DICOM to canvas:', {
      rows,
      cols,
      pixelDataLength: pixelData.length,
      canvasWidth: canvas.width,
      canvasHeight: canvas.height
    });
    
    // Set canvas size to match image
    canvas.width = cols;
    canvas.height = rows;
    
    const ctx = canvas.getContext('2d');
    const imageData = ctx.createImageData(cols, rows);
    
    // Convert DICOM pixel data to canvas ImageData
    // Handle different bit depths and pixel representations
    // Use iterative approach to avoid stack overflow with large arrays
    let minPixel = pixelData[0];
    let maxPixel = pixelData[0];
    for (let i = 1; i < pixelData.length; i++) {
      if (pixelData[i] < minPixel) minPixel = pixelData[i];
      if (pixelData[i] > maxPixel) maxPixel = pixelData[i];
    }
    const range = maxPixel - minPixel || 1; // Avoid division by zero
    
    console.log('Pixel value range:', { minPixel, maxPixel, range });
    
    // Check if this is a large image that needs batch processing
    const totalPixels = pixelData.length;
    const isLargeImage = totalPixels > 1000000; // More than 1M pixels
    
    if (isLargeImage) {
      console.log('Large image detected, using batch processing...');
      await renderPixelsBatched(pixelData, imageData.data, minPixel, range);
    } else {
      console.log('Small image, using direct processing...');
      renderPixelsDirect(pixelData, imageData.data, minPixel, range);
    }
    
    // Put the image data on the canvas
    ctx.putImageData(imageData, 0, 0);
    
    console.log('DICOM image rendered to canvas successfully');
    return true;
    
  } catch (error) {
    console.error('Error rendering DICOM to canvas:', error);
    return false;
  }
};

// Direct rendering for small images (< 1M pixels)
const renderPixelsDirect = (pixelData, imageDataArray, minPixel, range) => {
  if (range === 0) {
    // All pixels are the same value, make it visible
    for (let i = 0; i < pixelData.length; i++) {
      const pixelIndex = i * 4;
      imageDataArray[pixelIndex] = 128;     // R
      imageDataArray[pixelIndex + 1] = 128; // G  
      imageDataArray[pixelIndex + 2] = 128; // B
      imageDataArray[pixelIndex + 3] = 255; // A
    }
  } else {
    // Scale pixel values to 0-255 range
    for (let i = 0; i < pixelData.length; i++) {
      const normalizedValue = ((pixelData[i] - minPixel) / range) * 255;
      const pixelValue = Math.max(0, Math.min(255, Math.round(normalizedValue)));
      
      const pixelIndex = i * 4;
      imageDataArray[pixelIndex] = pixelValue;     // R
      imageDataArray[pixelIndex + 1] = pixelValue; // G  
      imageDataArray[pixelIndex + 2] = pixelValue; // B
      imageDataArray[pixelIndex + 3] = 255;        // A
    }
  }
};

// Batched rendering for large images (>= 1M pixels)
const renderPixelsBatched = async (pixelData, imageDataArray, minPixel, range) => {
  const batchSize = 50000; // Process 50k pixels at a time
  const totalPixels = pixelData.length;
  
  for (let start = 0; start < totalPixels; start += batchSize) {
    const end = Math.min(start + batchSize, totalPixels);
    
    // Process batch
    if (range === 0) {
      // All pixels are the same value, make it visible
      for (let i = start; i < end; i++) {
        const pixelIndex = i * 4;
        imageDataArray[pixelIndex] = 128;     // R
        imageDataArray[pixelIndex + 1] = 128; // G  
        imageDataArray[pixelIndex + 2] = 128; // B
        imageDataArray[pixelIndex + 3] = 255; // A
      }
    } else {
      // Scale pixel values to 0-255 range
      for (let i = start; i < end; i++) {
        const normalizedValue = ((pixelData[i] - minPixel) / range) * 255;
        const pixelValue = Math.max(0, Math.min(255, Math.round(normalizedValue)));
        
        const pixelIndex = i * 4;
        imageDataArray[pixelIndex] = pixelValue;     // R
        imageDataArray[pixelIndex + 1] = pixelValue; // G  
        imageDataArray[pixelIndex + 2] = pixelValue; // B
        imageDataArray[pixelIndex + 3] = 255;        // A
      }
    }
    
    // Yield control to browser to prevent freezing
    if (start + batchSize < totalPixels) {
      await new Promise(resolve => setTimeout(resolve, 1));
    }
  }
  
  console.log('Batch processing completed');
};

export const getDicomMetadataWithDaikon = (dicomImage) => {
  try {
    // Extract metadata with proper error handling for each field
    let patientName = 'Anonymous';
    let patientId = 'N/A';
    let studyDate = 'N/A';
    let studyTime = '';
    let modality = 'XR';
    let studyDescription = 'X-Ray Study';
    let seriesDescription = 'N/A';
    let windowWidth = 255;
    let windowCenter = 127;
    
    // Patient Name - clean up formatting
    try {
      const rawPatientName = dicomImage.getPatientName();
      if (rawPatientName && rawPatientName.trim()) {
        // Clean up DICOM name formatting (remove ^^^)
        patientName = rawPatientName.replace(/\^+/g, ' ').trim();
      }
    } catch (e) {
      console.warn('Could not extract patient name:', e.message);
    }
    
    // Patient ID
    try {
      const rawPatientId = dicomImage.getPatientID();
      if (rawPatientId && rawPatientId.trim()) {
        patientId = rawPatientId.trim();
      }
    } catch (e) {
      console.warn('Could not extract patient ID:', e.message);
    }
    
    // Study Date - handle Date objects properly
    try {
      const rawStudyDate = dicomImage.getStudyDate();
      if (rawStudyDate) {
        if (rawStudyDate instanceof Date) {
          studyDate = rawStudyDate.toLocaleDateString();
        } else {
          studyDate = rawStudyDate.toString();
        }
      }
    } catch (e) {
      console.warn('Could not extract study date:', e.message);
    }
    
    // Study Time
    try {
      const rawStudyTime = dicomImage.getStudyTime();
      if (rawStudyTime && rawStudyTime.toString().trim()) {
        const timeStr = rawStudyTime.toString();
        // Format HHMMSS to HH:MM:SS
        if (timeStr.length >= 6) {
          studyTime = `${timeStr.substring(0,2)}:${timeStr.substring(2,4)}:${timeStr.substring(4,6)}`;
        } else {
          studyTime = timeStr;
        }
      }
    } catch (e) {
      console.warn('Could not extract study time:', e.message);
    }
    
    // Modality
    try {
      const rawModality = dicomImage.getModality();
      if (rawModality && rawModality.trim()) {
        modality = rawModality.trim();
      }
    } catch (e) {
      console.warn('Could not extract modality:', e.message);
    }
    
    // Study Description - try multiple methods
    try {
      const rawStudyDescription = dicomImage.getStudyDescription && dicomImage.getStudyDescription();
      if (rawStudyDescription && rawStudyDescription.trim()) {
        studyDescription = rawStudyDescription.trim();
      } else {
        // Fallback based on modality
        const modalityDescriptions = {
          'PX': 'Panoramic X-Ray',
          'DX': 'Digital X-Ray', 
          'CR': 'Computed Radiography',
          'CT': 'Computed Tomography',
          'MR': 'Magnetic Resonance',
          'XR': 'X-Ray'
        };
        studyDescription = modalityDescriptions[modality] || 'Medical Image Study';
      }
    } catch (e) {
      console.warn('Could not extract study description:', e.message);
    }
    
    // Series Description
    try {
      const rawSeriesDescription = dicomImage.getSeriesDescription();
      if (rawSeriesDescription && rawSeriesDescription.trim()) {
        seriesDescription = rawSeriesDescription.trim();
      }
    } catch (e) {
      console.warn('Could not extract series description:', e.message);
    }
    
    // Window Width and Center
    try {
      const rawWindowWidth = dicomImage.getWindowWidth();
      if (rawWindowWidth && rawWindowWidth > 0) {
        windowWidth = rawWindowWidth;
      }
    } catch (e) {
      console.warn('Could not extract window width:', e.message);
    }
    
    try {
      const rawWindowCenter = dicomImage.getWindowCenter();
      if (rawWindowCenter !== undefined && rawWindowCenter !== null) {
        windowCenter = rawWindowCenter;
      }
    } catch (e) {
      console.warn('Could not extract window center:', e.message);
    }
    
    // Combine date and time for display
    let displayDateTime = studyDate;
    if (studyTime) {
      displayDateTime = `${studyDate} ${studyTime}`;
    }
    
    const metadata = {
      patientName: patientName,
      patientId: patientId,
      studyDate: displayDateTime,
      modality: modality,
      studyDescription: studyDescription,
      seriesDescription: seriesDescription,
      width: dicomImage.getCols() || 0,
      height: dicomImage.getRows() || 0,
      windowWidth: windowWidth,
      windowCenter: windowCenter
    };

    console.log('Successfully extracted DICOM metadata:', metadata);
    return metadata;
  } catch (error) {
    console.error('Error extracting DICOM metadata:', error);
    return {
      patientName: 'Error Loading',
      patientId: 'Error',
      studyDate: 'Error',
      modality: 'Unknown',
      studyDescription: 'Could not load study information',
      seriesDescription: 'Error',
      width: 0,
      height: 0,
      windowWidth: 255,
      windowCenter: 127
    };
  }
};

export const createDicomThumbnail = async (imageUrl) => {
  try {
    console.log('Creating DICOM thumbnail with Daikon (optimized)...');
    
    const dicomImage = await loadDicomWithDaikon(imageUrl);
    
    const rows = dicomImage.getRows();
    const cols = dicomImage.getCols();
    const pixelData = dicomImage.getInterpretedData();
    
    console.log('Creating thumbnail for image:', { rows, cols });
    
    // Create thumbnail maintaining aspect ratio
    const maxThumbSize = 150;
    const aspectRatio = cols / rows;
    let thumbWidth, thumbHeight;
    
    if (aspectRatio > 1) {
      // Landscape orientation
      thumbWidth = maxThumbSize;
      thumbHeight = Math.round(maxThumbSize / aspectRatio);
    } else {
      // Portrait or square orientation
      thumbHeight = maxThumbSize;
      thumbWidth = Math.round(maxThumbSize * aspectRatio);
    }
    
    console.log('Thumbnail dimensions:', { thumbWidth, thumbHeight, aspectRatio });
    
    const canvas = document.createElement('canvas');
    canvas.width = thumbWidth;
    canvas.height = thumbHeight;
    
    const ctx = canvas.getContext('2d');
    const imageData = ctx.createImageData(thumbWidth, thumbHeight);
    
    // Calculate sampling ratios
    const rowStep = rows / thumbHeight;
    const colStep = cols / thumbWidth;
    
    // Get pixel value range for scaling (iterative to avoid stack overflow)
    let minPixel = pixelData[0];
    let maxPixel = pixelData[0];
    for (let i = 1; i < pixelData.length; i++) {
      if (pixelData[i] < minPixel) minPixel = pixelData[i];
      if (pixelData[i] > maxPixel) maxPixel = pixelData[i];
    }
    const range = maxPixel - minPixel || 1; // Avoid division by zero
    
    console.log('Thumbnail pixel range:', { minPixel, maxPixel, range });
    
    // Sample pixels to create thumbnail
    for (let thumbY = 0; thumbY < thumbHeight; thumbY++) {
      for (let thumbX = 0; thumbX < thumbWidth; thumbX++) {
        // Calculate source pixel coordinates
        const srcY = Math.floor(thumbY * rowStep);
        const srcX = Math.floor(thumbX * colStep);
        const srcIndex = srcY * cols + srcX;
        
        // Get pixel value and scale to 0-255
        const pixelValue = pixelData[srcIndex];
        const normalizedValue = ((pixelValue - minPixel) / range) * 255;
        const scaledValue = Math.max(0, Math.min(255, Math.round(normalizedValue)));
        
        // Set thumbnail pixel
        const thumbIndex = (thumbY * thumbWidth + thumbX) * 4;
        imageData.data[thumbIndex] = scaledValue;     // R
        imageData.data[thumbIndex + 1] = scaledValue; // G
        imageData.data[thumbIndex + 2] = scaledValue; // B
        imageData.data[thumbIndex + 3] = 255;         // A
      }
    }
    
    // Put the thumbnail data on canvas
    ctx.putImageData(imageData, 0, 0);
    
    const thumbnailUrl = canvas.toDataURL('image/jpeg', 0.8);
    console.log('Successfully created optimized Daikon thumbnail');
    return thumbnailUrl;
    
  } catch (error) {
    console.error('Error creating optimized Daikon thumbnail:', error);
    return null;
  }
};

export const cleanupDaikon = () => {
  // Daikon doesn't require cleanup
  console.log('Daikon cleanup complete');
};